import { useEffect, useId, useReducer, useRef, useState, type RefObject } from 'react'
import { AlertTriangle, Banknote, BadgeCheck, CircleCheck, CircleDashed, CircleX, Clock, Link as LinkGlyph, type LucideIcon } from 'lucide-react'
import { Icon } from '../../platform/icons/Icon'
import { Overlay } from '../../platform/overlay/Overlay'
import { Button } from '../../platform/primitives/Button'
import { ConfirmDialog } from '../../platform/primitives/Dialog'
import { MerchantAvatar } from '../../platform/primitives/MerchantAvatar'
import { Money } from '../../platform/primitives/Money'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { ChoosePaymentSheet } from './ChoosePaymentSheet'
import { historyState, type HistoryTone } from './detailStats'
import { formatDue, periodAsShown, type LocalDate, type SheetWrites } from './recurringRules'
import type { Bill, Payment, Period } from './recurringWire'
import { PeriodLinkError, UNLINK_FAILED, usePeriodLinks, type Filed } from './usePeriodLinks'

/** iOS `RecurringPeriodSheet` and `RecurringCopy`, verbatim unless marked. */
export const PERIOD_COPY = {
  label: 'Period',
  done: 'Done',
  settledBy: 'Settled by',
  nothingLinked: 'Nothing is linked to this period yet.',
  link: 'Link a payment',
  unlink: 'Unlink this transaction',
  unlinkHint: 'A period holds one transaction. Unlink this one to record a different payment.',
  confirmTitle: 'Unlink this transaction from this period?',
  confirmBody: 'The transaction stays on your account. This period goes back to unpaid.',
  confirmLabel: 'Unlink',
  /** NEW COPY (owner review). */
  confirmPending: 'Unlinking…',
  /** NEW COPY (owner review): a cash payment has no merchant of its own. */
  cashPayee: 'Cash or another bank',
  /** NEW COPY (owner review): the server snapped the link onto another period. */
  filedElsewhere: (d: LocalDate) => `It was linked to the ${formatDue(d, 'short')} period instead.`,
} as const

export type Badge = { kind: 'amount-changed' | 'auto-matched' | 'cash'; label: string }

/**
 * The badges a settling payment earns, in the iOS order (`SettlementBadge`).
 *
 * Cash is `transactionId === null`, not `matchedBy === 'manual_cash'` — the
 * iOS correctness review found the two predicates could disagree when only
 * one field was set, and the missing transaction IS what makes it cash (the
 * wire's own note on `Payment.transactionId`).
 */
export function badgesFor(p: Payment): Badge[] {
  const out: Badge[] = []
  if (p.amountFlagged) out.push({ kind: 'amount-changed', label: 'Amount changed?' })
  if (p.matchedBy === 'auto') out.push({ kind: 'auto-matched', label: 'Auto-matched' })
  if (p.transactionId === null) out.push({ kind: 'cash', label: 'Paid (cash)' })
  return out
}

const BADGE_GLYPH: Record<Badge['kind'], LucideIcon> = {
  'amount-changed': AlertTriangle,
  'auto-matched': BadgeCheck,
  cash: Banknote,
}

const TONE_GLYPH: Record<HistoryTone, LucideIcon> = {
  paid: CircleCheck,
  late: Clock,
  missed: AlertTriangle,
  neutral: CircleDashed,
}

/** The headline is the state's word alone: the Settled-by card carries the date. */
function headline(p: Period, today: LocalDate, nextDue: LocalDate | null): { tone: HistoryTone; word: string } {
  const state = historyState(p, today, nextDue)
  if (state.tone === 'paid') return { tone: 'paid', word: 'Paid' }
  if (state.tone === 'late') return { tone: 'late', word: 'Paid late' }
  return { tone: state.tone, word: state.text }
}

const payeeOf = (p: Payment): string => p.merchant || (p.transactionId === null ? PERIOD_COPY.cashPayee : 'Transaction')

/** "Mar" and "22", read off the calendar date — never a `Date` (a UTC midnight shifts a day west of UTC). */
function tileOf(d: LocalDate): { month: string; day: string } {
  const [month = '', day = ''] = formatDue(d, 'short').split(' ')
  return { month, day }
}

function SettledBy({ payment, currency, headingRef }: { payment: Payment; currency: string; headingRef: RefObject<HTMLHeadingElement | null> }) {
  const headingId = useId()
  const badges = badgesFor(payment)
  const payee = payeeOf(payment)
  const paid = formatDue(payment.paidOn, 'short')
  const cents = payment.amountCents === null ? null : Math.abs(payment.amountCents)
  return (
    <section className="rec-period__settled" aria-labelledby={headingId}>
      <h3 id={headingId} ref={headingRef} tabIndex={-1} className="rec-period__label">
        {PERIOD_COPY.settledBy}
      </h3>
      {/* One unit for a screen reader (iOS `settledByAccessibilityLabel`); the
          words inside stay visible text, and the icons are decorative. */}
      <div className="rec-period__card" role="group" aria-labelledby={`${headingId} ${headingId}-who`}>
        <span id={`${headingId}-who`} className="rec-period__payment">
          <MerchantAvatar
            merchant={{ id: payment.transactionId ?? payment.id, name: payee, type: 'other', icon: null, icon_url: null, is_global: false }}
            size="row"
          />
          <span className="rec-period__who">
            <span className="rec-period__payee">{payee}</span>
            <span className="sr-only">,</span>{' '}
            <span className="rec-period__paid">Paid {paid}</span>
          </span>
          <span className="sr-only">,</span>{' '}
          <span className="rec-period__amount">
            {cents === null ? (
              <>
                <span aria-hidden="true">—</span>
                <span className="sr-only">amount not recorded</span>
              </>
            ) : (
              <Money cents={cents} currency={currency} />
            )}
          </span>
          {badges.length > 0 && <span className="sr-only">, {badges.map((b) => b.label).join(', ')}</span>}
        </span>
        {badges.length > 0 && (
          <ul className="rec-period__badges" aria-hidden="true">
            {badges.map((b) => (
              <li key={b.kind} className={`rec-period__badge rec-period__badge--${b.kind}`}>
                <Icon glyph={BADGE_GLYPH[b.kind]} size={12} />
                {b.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}

/** What the sheet is showing, and what it has done that the timeline may not show yet. */
type SheetState = SheetWrites & {
  view: 'period' | 'link'
  /** A link the server filed under ANOTHER period: said, never silent. */
  elsewhere: LocalDate | null
  /** Where focus goes after the next render, since the control it lands on may not exist yet. */
  focus: 'link' | 'settled' | null
}

type SheetAction =
  | { type: 'open-picker' }
  | { type: 'back' }
  | { type: 'linked'; filed: Filed; dueDate: LocalDate }
  | { type: 'unlinked'; paymentId: string }
  | { type: 'focused' }

const INITIAL: SheetState = { view: 'period', filed: null, unlinkedIds: [], elsewhere: null, focus: null }

function sheetReducer(state: SheetState, action: SheetAction): SheetState {
  switch (action.type) {
    case 'open-picker':
      return { ...state, view: 'link' }
    case 'back':
      return { ...state, view: 'period', focus: 'link' }
    case 'linked': {
      // Only a payment filed under THIS period settles it: the server snaps
      // the date onto its grid, and one filed elsewhere is that period's.
      const here = action.filed.dueDate === null || action.filed.dueDate === action.dueDate
      return {
        ...state,
        view: 'period',
        filed: here ? action.filed.payment : state.filed,
        // Unlinked ids are KEPT: the timeline may still hold the old payment.
        elsewhere: here ? null : action.filed.dueDate,
        focus: here ? 'settled' : 'link',
      }
    }
    case 'unlinked':
      return { ...state, unlinkedIds: [...state.unlinkedIds, action.paymentId], focus: 'link' }
    case 'focused':
      return { ...state, focus: null }
  }
}

/**
 * The period sheet (Task 10, D5, iOS Q17; shape `m7-shape-recurring-period-sheet`),
 * opened from a payment-history row. It reads like a receipt: the period, what
 * settled it, and the one correction available.
 *
 * - **Settled** — "Settled by" with the payment's merchant, date, amount and
 *   badges (Amount changed?, Auto-matched, Paid (cash)), then **Unlink this
 *   transaction**, confirmed first. Cancel sends nothing; a cash payment takes
 *   the same flow (the server deletes it).
 * - **Empty** — "Nothing is linked to this period yet." and **Link a payment**,
 *   which swaps this content in place for the Choose-payment list in `link`
 *   mode (one modal, one focus trap), centred on THIS period's due date.
 *
 * **It follows the live row** (iOS `currentRow`): `period` is the timeline's
 * current entry, so a refetch re-renders it. Between a write's answer and that
 * refetch, the sheet shows what the server said — the payment a link filed
 * here, or the gap an unlink left — so it never flashes the old variant.
 *
 * Focus: the empty variant's Link button after an unlink (the Unlink button is
 * gone) and after Back; the Settled-by heading after a link.
 *
 * Phone: the bottom sheet (Ruling 15). Wider: the centred dialog. No motion
 * beyond Overlay's own entry (Ruling 14).
 */
export function PeriodSheet({
  bill,
  period,
  today,
  onClose,
}: {
  bill: Bill
  period: Period
  today: LocalDate
  onClose: () => void
}) {
  const phone = useBreakpoint() === 'phone'
  const { unlink } = usePeriodLinks(bill)
  const [state, dispatch] = useReducer(sheetReducer, INITIAL)
  const { view, elsewhere, focus } = state
  const [confirming, setConfirming] = useState(false)
  const linkButton = useRef<HTMLButtonElement>(null)
  const settledHeading = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    if (focus === null) return
    ;(focus === 'link' ? linkButton : settledHeading).current?.focus()
    dispatch({ type: 'focused' })
  }, [focus, view])

  // The period as it stands after this sheet's own writes, until the refetch agrees.
  const shown = periodAsShown(period, state, today)
  const payment = shown.payment
  const { tone, word } = headline(shown, today, bill.isActive ? bill.nextDue : null)
  const tile = tileOf(period.dueDate)

  return (
    <Overlay open onClose={onClose} label={`${PERIOD_COPY.label}, ${formatDue(period.dueDate, 'long')}`} placement={phone ? 'bottom' : 'center'}>
      {view === 'link' ? (
        <ChoosePaymentSheet
          mode="link"
          bill={bill}
          dueDate={period.dueDate}
          today={today}
          onBack={() => dispatch({ type: 'back' })}
          onLinked={(filed) => dispatch({ type: 'linked', filed, dueDate: period.dueDate })}
        />
      ) : (
        <div className="rec-period">
          <div className="rec-period__head">
            <span className="rec-period__tile" aria-hidden="true">
              <span className="rec-period__tile-month">{tile.month}</span>
              <span className="rec-period__tile-day">{tile.day}</span>
            </span>
            <div className="rec-period__heading">
              <h2 className="rec-period__status">
                <span className={`rec-period__mark rec-period__mark--${tone}`}>
                  <Icon glyph={TONE_GLYPH[tone]} size={18} />
                </span>
                {word}
              </h2>
              <p className="rec-period__meta">
                Due {formatDue(period.dueDate, 'long')} · <Money cents={bill.expectedCents} currency={bill.currency} /> expected
              </p>
            </div>
            <Button variant="chip" size="panel" className="rec-period__done" onClick={onClose}>
              {PERIOD_COPY.done}
            </Button>
          </div>

          {payment !== null ? (
            <>
              <SettledBy payment={payment} currency={bill.currency} headingRef={settledHeading} />
              <div className="rec-period__action">
                <Button variant="destructive" size="panel" className="rec-period__cta" onClick={() => setConfirming(true)}>
                  <Icon glyph={CircleX} size={16} />
                  {PERIOD_COPY.unlink}
                </Button>
                <p className="note rec-period__hint">{PERIOD_COPY.unlinkHint}</p>
              </div>
            </>
          ) : (
            <div className="rec-period__action">
              <p className="note rec-period__empty">{PERIOD_COPY.nothingLinked}</p>
              {elsewhere !== null && (
                <p className="note rec-period__elsewhere" role="status">
                  {PERIOD_COPY.filedElsewhere(elsewhere)}
                </p>
              )}
              <Button ref={linkButton} variant="primary" size="panel" className="rec-period__cta" onClick={() => dispatch({ type: 'open-picker' })}>
                <Icon glyph={LinkGlyph} size={16} />
                {PERIOD_COPY.link}
              </Button>
            </div>
          )}
        </div>
      )}
      {confirming && payment !== null && (
        <ConfirmDialog
          open
          destructive
          mode="unlink_recurring_payment"
          title={PERIOD_COPY.confirmTitle}
          body={PERIOD_COPY.confirmBody}
          confirmLabel={PERIOD_COPY.confirmLabel}
          pendingLabel={PERIOD_COPY.confirmPending}
          errorFor={(e) => (e instanceof PeriodLinkError ? e.message : UNLINK_FAILED)}
          // The Unlink button is gone once this succeeds: land on what replaced it.
          restoreFocusTo={linkButton}
          onCancel={() => setConfirming(false)}
          onConfirm={async () => {
            await unlink(payment)
            dispatch({ type: 'unlinked', paymentId: payment.id })
            setConfirming(false)
          }}
        />
      )}
    </Overlay>
  )
}
